import axios from 'axios';
import { ORDER_GENIE_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';

// Story 2.1: the CustomerIdentity embedded admin area (its own CiAdminAuthProvider
// context) calls into order_genie_service's import endpoint -- a cross-area call.
// Confirmed during story creation: both orderXApi.js and customerIdentityApi.js
// authenticate via the identical getAuthHeadersFromStorage() utility (the main
// app's OAuth/API-key), so a plain client pointed at ORDER_GENIE_API_BASE_URL
// works unchanged from inside the CI admin context -- no adapter needed.
const client = axios.create({
  baseURL: ORDER_GENIE_API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const authHeaders = getAuthHeadersFromStorage();
  if (Object.keys(authHeaders || {}).length) {
    config.headers = { ...authHeaders, ...config.headers };
  }
  return config;
});

const extractError = (error) => (
  error?.response?.data?.detail
  || error?.response?.data?.message
  || error?.message
  || 'Request failed'
);

// Target field paths the mapping canvas may offer -- mirrors the backend's
// IMPORT_TARGET_FIELDS allowlist (order_genie_service/services/customer_directory/
// customer_import_service.py) exactly; kept here rather than fetched from the
// server since this is a fixed, small schema, not account-specific data.
export const IMPORT_TARGET_FIELD_GROUPS = [
  {
    entity: 'customer',
    label: 'Customer Fields',
    fields: [
      { path: 'customer.name', label: 'Name', required: true },
      { path: 'customer.erp_customer_id', label: 'ERP Customer ID' },
      { path: 'customer.external_customer_id', label: 'Web Customer ID' },
      { path: 'customer.email', label: 'Email' },
      { path: 'customer.phone', label: 'Phone' },
    ],
  },
  {
    entity: 'billto',
    label: 'Bill-To Address Fields',
    fields: [
      { path: 'billto.address_line1', label: 'Address Line 1', required: true },
      { path: 'billto.city', label: 'City', required: true },
      { path: 'billto.address_line2', label: 'Address Line 2' },
      { path: 'billto.state_province', label: 'State / Province' },
      { path: 'billto.postal_code', label: 'Postal Code' },
      { path: 'billto.country_code', label: 'Country Code' },
      { path: 'billto.label', label: 'Label' },
      { path: 'billto.contact_name', label: 'Contact Name' },
      { path: 'billto.company', label: 'Company' },
      { path: 'billto.external_id', label: 'External Bill-To ID' },
    ],
  },
  {
    entity: 'address',
    label: 'Ship-To Address Fields',
    fields: [
      { path: 'address.address_line1', label: 'Address Line 1', required: true },
      { path: 'address.city', label: 'City', required: true },
      { path: 'address.address_line2', label: 'Address Line 2' },
      { path: 'address.state_province', label: 'State / Province' },
      { path: 'address.postal_code', label: 'Postal Code' },
      { path: 'address.country_code', label: 'Country Code' },
      { path: 'address.label', label: 'Label' },
      { path: 'address.contact_name', label: 'Contact Name' },
      { path: 'address.company', label: 'Company' },
      { path: 'address.external_shipto_id', label: 'External Ship-To ID' },
    ],
  },
  {
    entity: 'contact',
    label: 'Contact Fields',
    fields: [
      { path: 'contact.email', label: 'Email', required: true },
      { path: 'contact.first_name', label: 'First Name' },
      { path: 'contact.last_name', label: 'Last Name' },
      { path: 'contact.phone', label: 'Phone' },
      { path: 'contact.external_user_id', label: 'External Contact ID' },
    ],
  },
];

// Groups each entity from IMPORT_TARGET_FIELD_GROUPS above into the three
// numbered, visually separated sections the mapping canvas renders -- the
// admin works through them in this order: a customer record (with its
// bill-to address), then its ship-to address, then its contacts.
export const IMPORT_SECTIONS = [
  { key: 'customer', title: '1. Customer & Bill-To Address', entities: ['customer', 'billto'] },
  { key: 'shipto', title: '2. Ship-To Address', entities: ['address'] },
  { key: 'contact', title: '3. Contacts', entities: ['contact'] },
];

// Kicks off the import as an async background job -- returns immediately
// with {job_id, status: "queued", totals}; poll getCustomerImportJob(jobId)
// for progress and the final summary. A 409 means a job for this site is
// already running; the server's error payload includes that job's id/state
// (error.response.data.detail.job) so the caller can resume polling it
// instead of treating it as a hard failure.
export const startCustomerImportJob = async ({
  site_id: siteId, headers, rows, mapping,
}) => {
  try {
    const { data } = await client.post('/api/v1/orderx/import/customer-data/jobs', {
      site_id: siteId,
      headers,
      rows,
      mapping,
    });
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error.response.data?.detail?.job) {
      return { ...error.response.data.detail.job, resumed: true };
    }
    throw new Error(extractError(error));
  }
};

// Poll endpoint for the job started above. Returns the full job row:
// {job_id, site_id, status, phase, progress, summary, error, started_at, finished_at, updated_at}.
export const getCustomerImportJob = async (jobId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/import/customer-data/jobs/${jobId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Job List UI feed: paginated, most-recent-first. Returns
// {jobs: [{job_id, site_id, status, phase, progress, summary, error, created_by,
// started_at, finished_at, updated_at}, ...], total}. date_from/date_to are
// 'YYYY-MM-DD' strings, inclusive, matched against started_at.
export const listCustomerImportJobs = async ({
  site_id: siteId, search, date_from: dateFrom, date_to: dateTo, limit, offset,
} = {}) => {
  try {
    const { data } = await client.get('/api/v1/orderx/import/customer-data/jobs', {
      params: {
        site_id: siteId || undefined,
        search: search || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit,
        offset,
      },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Full reindex: every active customer + ship-to address for the account,
// into the per-account customer_directory OpenSearch collection. Runs as a
// background job (can touch thousands of rows) -- returns {job_id, status,
// mode} immediately, poll getCustomerDirectoryReindexJob for progress. A 409
// means one is already running; resolves to that job instead of throwing so
// the UI can just show/poll it rather than erroring.
export const startCustomerDirectoryFullReindex = async () => {
  try {
    const { data } = await client.post('/api/v1/orderx/customer-directory/reindex/full');
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error.response.data?.detail?.job) {
      return { ...error.response.data.detail.job, resumed: true };
    }
    throw new Error(extractError(error));
  }
};

// Delta reindex: only customers/ship-to addresses changed since the last
// successful full or delta reindex for this account (falls back to a full
// reindex if this account has never been reindexed). Same job/poll contract
// as the full reindex above.
export const startCustomerDirectoryDeltaReindex = async () => {
  try {
    const { data } = await client.post('/api/v1/orderx/customer-directory/reindex/delta');
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error.response.data?.detail?.job) {
      return { ...error.response.data.detail.job, resumed: true };
    }
    throw new Error(extractError(error));
  }
};

// Poll endpoint for a job started by either reindex function above.
export const getCustomerDirectoryReindexJob = async (jobId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/customer-directory/reindex/jobs/${jobId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listCustomerDirectoryReindexJobs = async ({ limit, offset } = {}) => {
  try {
    const { data } = await client.get('/api/v1/orderx/customer-directory/reindex/jobs', {
      params: { limit, offset },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Real-time reindex of one or a few specific rows -- call this right after
// a single customer/ship-to edit saves elsewhere in the UI, so the
// OpenSearch typeahead reflects the change immediately instead of waiting
// for the next delta/full reindex. Synchronous (not a job) -- resolves with
// {indexed: N}. Swallows its own errors into a resolved {indexed: 0, error}
// rather than throwing, since a failed background reindex should never
// block or roll back the edit the caller just successfully saved.
export const reindexCustomerDirectoryRows = async (entityType, ids) => {
  const cleanIds = (ids || []).filter(Boolean);
  if (!cleanIds.length) {
    return { indexed: 0 };
  }
  try {
    const { data } = await client.post('/api/v1/orderx/customer-directory/reindex/row', {
      entity_type: entityType,
      ids: cleanIds,
    });
    return data;
  } catch (error) {
    return { indexed: 0, error: extractError(error) };
  }
};

// Exports Customer/Ship-To/Contact data in the same wide-row shape
// importCustomerData() accepts ({headers, rows, mapping}), so the result of
// this call can be fed straight into a spreadsheet, edited, and re-uploaded
// through the same import UI unchanged.
export const exportCustomerData = async ({ site_id: siteId }) => {
  try {
    const { data } = await client.get('/api/v1/orderx/export/customer-data', {
      params: { site_id: siteId },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Story 2.2: saved {site_id, mapping, source_url} sync profiles, re-run on a
// schedule via Cronicle. Only `interval_minutes` scheduling is exposed in
// this first UI pass (see the story's Dev Notes) -- the backend/DB shape
// also supports hourly/daily/weekly, for a future scheduling UI.
export const createSyncProfile = async ({
  site_id: siteId, source_url: sourceUrl, mapping, interval_minutes: intervalMinutes, enabled,
}) => {
  try {
    const { data } = await client.post('/api/v1/orderx/sync-profiles', {
      site_id: siteId,
      source_url: sourceUrl,
      mapping,
      frequency_type: 'interval',
      interval_minutes: intervalMinutes,
      minute: 0,
      enabled,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listSyncProfiles = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/sync-profiles');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const runSyncProfileNow = async (profileId) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/sync-profiles/${profileId}/run`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSyncProfile = async (profileId) => {
  try {
    const { data } = await client.delete(`/api/v1/orderx/sync-profiles/${profileId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Story 2.3: open address-sync conflicts awaiting admin review (AD-8).
export const listSyncConflicts = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/sync-conflicts');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const resolveSyncConflict = async (conflictId) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/sync-conflicts/${conflictId}/resolve`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Story 4.1: Learned Associations admin view + removal (soft-disable).
export const listLearnedAssociations = async (matchType) => {
  try {
    const { data } = await client.get('/api/v1/orderx/learned-associations', {
      params: matchType ? { match_type: matchType } : {},
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const removeLearnedAssociation = async (recordId) => {
  try {
    const { data } = await client.delete(`/api/v1/orderx/learned-associations/${recordId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Manual edit/add: author a correction directly, or fix an existing one,
// without a real order's override driving it.
export const createLearnedAssociation = async ({ match_type: matchType, old_value: oldValue, new_value: newValue }) => {
  try {
    const { data } = await client.post('/api/v1/orderx/learned-associations', {
      match_type: matchType,
      old_value: oldValue,
      new_value: newValue,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateLearnedAssociation = async (recordId, { match_type: matchType, old_value: oldValue, new_value: newValue }) => {
  try {
    const { data } = await client.patch(`/api/v1/orderx/learned-associations/${recordId}`, {
      match_type: matchType,
      old_value: oldValue,
      new_value: newValue,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};
